/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import UploadFilesModal from "@eisinc/App/Components/UploadFilesModal/UploadFilesModal";
import customRequestQuote from "@eisinc/App/Handlers/CustomRequestQuote";
import Select, { SelectPresentationProps } from "@eisinc/MobiusOverrides/Select";
import TextArea, { TextAreaProps } from "@eisinc/MobiusOverrides/TextArea/TextArea";
import TextField, { TextFieldPresentationProps } from "@eisinc/MobiusOverrides/TextField";
import { screen } from "@eisinc/XDL/Helpers";
import { QuoteType } from "@insite/client-framework/Services/QuoteService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import createOrRequestQuote from "@insite/client-framework/Store/Pages/RfqRequestQuote/Handlers/CreateOrRequestQuote";
import updateQuoteParameter from "@insite/client-framework/Store/Pages/RfqRequestQuote/Handlers/UpdateQuoteParameter";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup/RadioGroup";
import FieldSetPresentationProps from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { ChangeEvent, FC, ReactNode, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    cart: getCurrentCartState(state).value,
    accounts: state.pages.rfqRequestQuote.accounts,
    rfqQuoteDetailsPageUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
    rfqQuoteConfirmationPageUrl: getPageLinkByPageType(state, "RfqConfirmationPage")?.url,
    jobQuoteEnabled: getSettingsCollection(state).quoteSettings.jobQuoteEnabled,
    quoteParameter: state.pages.rfqRequestQuote.quoteParameter,
    isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
});

const mapDispatchToProps = {
    updateQuoteParameter,
    createOrRequestQuote,
    customRequestQuote,
};

type Props = ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps> & WidgetProps;

export interface RfqRequestQuoteSpecificationsStyles {
    quoteTypeRadioGroup?: RadioGroupProps;
    quoteTypeRadioButton?: FieldSetPresentationProps<RadioComponentProps>;
    jobNameTextField?: TextFieldPresentationProps;
    assignUserSelect?: SelectPresentationProps;
    notesTextArea?: TextAreaProps;
    requisitionNumber?: TextFieldPresentationProps;
    uploadDocumentsCheckbox?: CheckboxPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const rfqRequestQuoteSpecificationsStyles: RfqRequestQuoteSpecificationsStyles = {
    quoteTypeRadioGroup: {
        css: css`
            display: inline-block;
            padding-bottom: 20px;
            width: 100%;
            flex-direction: row;

            & > div {
                margin-right: 20px;
                display: inline-flex;
            }
        `,
    },
    jobNameTextField: {
        cssOverrides: {
            formField: css`
                padding-bottom: 20px;
            `,
        },
    },
    assignUserSelect: {
        cssOverrides: {
            formField: css`
                padding-bottom: 20px;
            `,
        },
    },
    notesTextArea: {
        cssOverrides: {
            formField: css`
                margin-bottom: 10px;
            `,
            inputSelect: css`
                resize: none;
            `,
        },
    },
    requisitionNumber: {
        cssOverrides: {
            formField: css`
                margin-bottom: 20px;
            `,
        },
    },
    uploadDocumentsCheckbox: {
        css: css`
            margin-bottom: 10px;
            ${screen("lt", "sm")` align-items: start; `}
            > span {
                border-radius: 0 !important;
                border: 2px solid ${getColor("common.border")} !important;
            }
        `,
    },
    submitButton: {
        css: css`
            width: 100%;
            margin-top: 15px;
        `,
    },
};

const styles = rfqRequestQuoteSpecificationsStyles;

const RfqRequestQuoteSpecifications: FC<Props> = ({
    cart,
    accounts,
    updateQuoteParameter,
    createOrRequestQuote,
    customRequestQuote,
    history,
    rfqQuoteConfirmationPageUrl,
    jobQuoteEnabled,
    quoteParameter,
    isUpdatingCartLine,
}) => {
    const [notes, setNotes] = useState(cart?.notes);
    const [jobName, setJobName] = useState("");
    const [jobNameError, setJobNameError] = useState<ReactNode>("");
    const [quoteType, setQuoteType] = useState<QuoteType>("quote");
    const [accountId, setAccountId] = useState<string | undefined>(undefined);
    const [accountIdError, setAccountIdError] = useState<ReactNode>("");
    const [requisitionNumber, setRequisitionNumber] = React.useState("");
    const [uploadDocuments, setUploadDocuments] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [openDocumentsModal, setOpenDocumentsModal] = useState(false);
    const [returnedFiles, setReturnedFiles] = useState([]);

    const uploadFilesModalRef: any = useRef(null);

    const saveParameter = () => {
        updateQuoteParameter({
            quoteId: cart?.id || "",
            quoteType,
            accountId,
            jobName,
            note: notes || "",
            requisitionNumber: requisitionNumber || "",
        } as any);
    };

    useEffect(saveParameter, [notes, requisitionNumber, jobName, quoteType, accountId, cart?.id]);

    // this is a work around to save the values of the comments and the requisition number on cartificatio or cart update
    useEffect(() => {
        if (quoteParameter.note !== "") {
            setNotes(quoteParameter.note);
        }
        if ((quoteParameter as any).requisitionNumber !== "") {
            setRequisitionNumber((quoteParameter as any).requisitionNumber);
        }
    }, [cart]);

    if (!cart) {
        return null;
    }

    const isCreatingQuote = cart.isSalesperson;

    const notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };

    const quoteTypeChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setQuoteType(event.currentTarget.value as QuoteType);
    };

    const userChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        const id = event.currentTarget.value;

        setAccountId(id);

        validateUser(id);
    };

    const validateUser = (userId: string) => {
        const valid = !isCreatingQuote || userId.trim();

        setAccountIdError(valid ? "" : siteMessage("Rfq_UserIsRequired"));

        return valid;
    };

    const jobNameChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const name = event.currentTarget.value;

        setJobName(name);
        validateJobName(name);
    };

    const validateJobName = (jobName: string) => {
        const valid = quoteType !== "job" || jobName.trim();

        setJobNameError(valid ? "" : siteMessage("Rfq_Job_Name_required"));

        return valid;
    };

    const canSubmit = cart.cartLines && cart.cartLines.length > 0 && !isUpdatingCartLine;

    const submitHandler = () => {
        setIsSubmitting(true);

        if (!canSubmit || !validateJobName(jobName) || !validateUser(accountId || "") || !notes) {
            return;
        }

        const poNumber = requisitionNumber;

        createOrRequestQuote({
            poNumber,
            onSuccess: (result: any) => {
                setIsSubmitting(false);

                const quoteNumber = result.orderNumber;
                const id = result.id;
                const formData = buildFormDataForDocuments(quoteNumber);

                customRequestQuote({
                    formData,
                    onSuccess: result => {
                        history.push(`${rfqQuoteConfirmationPageUrl}?quoteId=${id}`);
                    },
                    onError: result => {
                        history.push(`${rfqQuoteConfirmationPageUrl}?quoteId=${id}`);
                    },
                });
            },
        } as any);
    };

    const buildFormDataForDocuments = (number: string) => {
        const formData = new FormData();

        for (let i = 0; i < returnedFiles.length; i++) {
            const file = returnedFiles[i] as any;

            formData.append(`file[${i}]`, file, file.name || "");
        }

        formData.append(`quoteNumber`, number);

        return formData;
    };

    const requisitionNumberChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const requisitionNumber = event.currentTarget.value;

        setRequisitionNumber(requisitionNumber);
    };

    const submitButtonClickHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();

        submitHandler();
    };

    return (
        <form onSubmit={submitHandler}>
            {jobQuoteEnabled && (
                <RadioGroup
                    {...styles.quoteTypeRadioGroup}
                    label={translate("Quote Type")}
                    value={quoteType}
                    onChangeHandler={quoteTypeChangeHandler}
                    data-test-selector="requestQuoteTypeRadio"
                >
                    <Radio {...styles.quoteTypeRadioButton} value="quote">
                        {translate("Sales Quote")}
                    </Radio>
                    <Radio {...styles.quoteTypeRadioButton} value="job">
                        {translate("Job Quote")}
                    </Radio>
                </RadioGroup>
            )}
            {quoteType === "job" && (
                <TextField
                    {...styles.jobNameTextField}
                    label={translate("Job Name")}
                    required={true}
                    error={jobNameError}
                    placeholder={translate("Job Name Goes Here")}
                    onChange={jobNameChangeHandler}
                    data-test-selector="requestQuoteJobName"
                />
            )}
            {isCreatingQuote && (
                <Select
                    label={translate("Assign User")}
                    required={true}
                    {...styles.assignUserSelect}
                    value={accountId}
                    error={accountIdError}
                    onChange={userChangeHandler}
                    data-test-selector="requestQuoteAssignUserSelect"
                >
                    <option key="" value="">
                        {translate("Select a User")}
                    </option>
                    {accounts.map(o => {
                        return (
                            <option key={o.id} value={o.id}>
                                {o.firstName && o.lastName ? `${o.firstName} ${o.lastName}` : o.userName}
                            </option>
                        );
                    })}
                </Select>
            )}
            <TextArea
                {...styles.notesTextArea}
                label={translate("Comments ")}
                placeholder={translate("You can start typing notes here")}
                value={notes}
                required
                error={isSubmitting && !notes ? translate("Required") : null}
                minLength={1}
                maxLength={4000}
                onChange={notesChangeHandler}
                data-test-selector="requestQuoteOrderNotes"
            />
            <TextField
                {...styles.requisitionNumber}
                type="text"
                value={requisitionNumber}
                placeholder={translate("Type a PO number, optional")}
                onChange={requisitionNumberChangeHandler}
                maxLength={50}
                label={translate("Requisition #")}
            />

            <Checkbox
                {...styles.uploadDocumentsCheckbox}
                onChange={(e, value) => {
                    setUploadDocuments(value);
                    setOpenDocumentsModal(value);

                    if (!value) {
                        uploadFilesModalRef.current.resetFields();
                    }
                }}
                checked={uploadDocuments}
            >
                {translate("Click Here if would like to attach additional documents")}
            </Checkbox>
            <UploadFilesModal
                ref={uploadFilesModalRef}
                openModal={openDocumentsModal}
                headline={translate("Upload Documents")}
                handleClose={() => {
                    setOpenDocumentsModal(false);
                    setUploadDocuments(false);
                }}
                handleContinue={files => {
                    setOpenDocumentsModal(false);
                    setReturnedFiles(files);

                    if (!files.length) {
                        setUploadDocuments(false);
                    }
                }}
            />
            {canSubmit && (
                <Button
                    {...styles.submitButton}
                    onClick={submitButtonClickHandler}
                    data-test-selector="requestQuoteSubmitButton"
                >
                    {translate(isCreatingQuote ? "Create Quote" : "Submit Quote")}
                </Button>
            )}
        </form>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RfqRequestQuoteSpecifications)),
    definition: {
        group: "RFQ Request Quote",
        displayName: "Specifications",
        allowedContexts: ["RfqRequestQuotePage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
